import { Disclosure } from '@headlessui/react';
import clsx from 'clsx';
import parse from 'html-react-parser';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button as EditorButton } from '~components/generic/editor/Button/Button';
import { FormElementTextarea } from '~components/generic/editor/FormElementTextarea';
import { Checkbox } from '~components/generic/elements/Checkbox/Checkbox';
import RouterLink from '~components/generic/RouterLink';
import { CaretDownIcon, CaretUpIcon, RenameIcon, SaveIcon } from '~components/icons';
import { useChangelog } from '~extension/changelog/hooks/useChangelog';
import { useNewsletter } from '~extension/changelog/hooks/useNewsletter';
import useLabels from '~hooks/useLabels';
import { DeleteItemPopover } from './DeleteItemPopover';
import styles from './ListItem.module.css';

type ListItemProps = {
  item: ChangelogItem;
  showCheckbox?: boolean;
  onSelect?: (selected: boolean) => void;
  selected?: boolean;
};

export function ListItem({ item }: ListItemProps) {
  const {
    msgChangelogItemAlreadySent,
    btnChangelogItemSave,
    btnChangelogItemCancel,
    btnChangelogItemEdit,
    btnChangelogItemLink,
  } = useLabels([
    'msgChangelogItemAlreadySent',
    'btnChangelogItemSave',
    'btnChangelogItemCancel',
    'btnChangelogItemEdit',
    'btnChangelogItemLink',
  ]);
  const intl = useIntl();
  const { canEdit, updateChangelogItem, deleteChangelogItem } = useChangelog();
  const { isComposingNewsletter, selectedChangelogItemIds, toggleSelectedChangelogItemId } = useNewsletter();

  const [isEditing, setIsEditing] = useState(false);
  const [updatedDescription, setUpdatedDescription] = useState<ChangelogItem['description']>(null);

  // derived values
  const isSelected = useMemo(() => selectedChangelogItemIds.includes(item.id), [item.id, selectedChangelogItemIds]);

  function handleEdit() {
    setIsEditing(true);
  }

  function handleSave() {
    if (updatedDescription !== null) {
      updateChangelogItem(item.id, updatedDescription);
    }
    setIsEditing(false);
  }

  function handleDelete() {
    deleteChangelogItem(item.id);
  }

  function handleCancel() {
    setUpdatedDescription(null);
    setIsEditing(false);
  }

  function handleSelect() {
    toggleSelectedChangelogItemId(item.id);
  }

  return (
    <Disclosure>
      {({ open }) => (
        <div
          className={clsx(
            styles.item,
            'changelog-item',
            'flex flex-col border-t border-solid',
            open && styles.itemOpen,
            open && 'open',
          )}
        >
          <Disclosure.Button
            className={clsx(
              styles.header,
              'changelog-item--header',
              'flex justify-end items-center transition-all border-none cursor-pointer text-left',
            )}
          >
            {isComposingNewsletter && (
              <Checkbox
                value={isSelected}
                onChange={handleSelect}
                className={clsx(styles.checkboxSelect, 'changelog-item--select')}
              />
            )}
            <div className={clsx(styles.title, 'changelog-item--title', 'flex-grow font-semibold')}>
              <span>{item.pageTitle}</span>
              {isComposingNewsletter && item.sent && (
                <span className={clsx(styles.sent, 'changelog-item--sent', 'font-normal')}>
                  {msgChangelogItemAlreadySent}
                </span>
              )}
            </div>
            <div className={clsx(styles.date, 'changelog-item--date', 'whitespace-nowrap')}>
              {intl.formatDate(item.timestamp, { dateStyle: 'long', timeStyle: 'short' })}
            </div>
            <div className={clsx(styles.caret, 'changelog-item--caret', 'flex-shrink-0')}>
              {open ? <CaretDownIcon /> : <CaretUpIcon />}
            </div>
          </Disclosure.Button>
          <Disclosure.Panel className={clsx(styles.panel, 'changelog-item--panel', 'flex flex-col transition-all')}>
            {isEditing ? (
              <>
                <div className={clsx(styles.description, 'changelog-item--description')}>
                  <FormElementTextarea value={item.description} onChange={setUpdatedDescription} controls={[]} />
                </div>
                <div className={clsx(styles.actions, 'changelog-item--actions')}>
                  <EditorButton
                    className={clsx(styles.btnSave, 'changelog-item--btn-save', '')}
                    onClick={handleSave}
                    label={btnChangelogItemSave}
                    icon={<SaveIcon />}
                  />
                  <EditorButton
                    className={clsx(styles.btnCancel, 'changelog-item--btn-cancel', '')}
                    onClick={handleCancel}
                    label={btnChangelogItemCancel}
                  />
                </div>
              </>
            ) : (
              <>
                {canEdit && !!item.originalDescription && (
                  <div
                    className={clsx(
                      styles.originalDescription,
                      'changelog-item--original-description',
                      'opacity-50 line-through',
                    )}
                  >
                    {parse(item.originalDescription)}
                  </div>
                )}
                <div className={clsx(styles.description, 'changelog-item--description')}>
                  {item.description && parse(item.description)}
                </div>
                <div className={clsx(styles.actions, 'changelog-item--actions', 'flex items-center')}>
                  <RouterLink
                    className={clsx(
                      styles.btnLink,
                      'changelog-item--btn-link',
                      'inline-block border border-solid leading-loose no-underline',
                    )}
                    to={item.url}
                  >
                    {btnChangelogItemLink}
                  </RouterLink>
                  {canEdit && (
                    <>
                      <EditorButton
                        className={clsx(styles.btnEdit, 'changelog-item--btn-edit', '')}
                        label={btnChangelogItemEdit}
                        onClick={handleEdit}
                        icon={<RenameIcon />}
                      />
                      <DeleteItemPopover onDelete={handleDelete} />
                    </>
                  )}
                </div>
              </>
            )}
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  );
}
